import { keyframes } from '@emotion/react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { Box, Button, ButtonBase, CircularProgress, Collapse, LinearProgress, Stack, Typography } from '@mui/material';
import React from 'react';
import { CharacterGrade, DEFAULT_TAKES, gradesForOutcome, ScoreOutcome, scoreMessage, speakingScore } from '../../mockLessonContent';
import { BLUE, ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, RED, RING_TRACK, SLATE, yellowButtonSx } from '../../studentStyles';
import ProgressRing from '../ProgressRing';
import PronunciationDetail from './PronunciationDetail';
import MediaErrorNotice from '../MediaErrorNotice';
import useMockMediaFailure, { MediaFailure } from '../useMockMediaFailure';

const recordingDot = keyframes`
    0% { opacity: 1; }
    50% { opacity: 0.25; }
    100% { opacity: 1; }
`;

const PROCESSING_MS = 1400;

// graded   — Good passes; other results offer retry, with Skip after two unsuccessful takes.
// feedback — scored, but any take completes (Listen & Speak, No Grade).
// none     — not scored at all, only acknowledged (Speak Anything, No Grade).
export type Scoring = 'graded' | 'feedback' | 'none';

type Phase = 'idle' | 'recording' | 'processing' | 'result';

interface RecordAnswerPanelProps {
    grades: CharacterGrade[];
    scoring?: Scoring;
    // Scripted outcomes for the mock; the last one repeats.
    takes?: ScoreOutcome[];
    idleLabel?: string;
    // Tighter layout for the record control inside a conversation line.
    compact?: boolean;
    disabled?: boolean;
    disabledHint?: string;
    // Teacher-authored responses (spoken_yes_response / spoken_no_response).
    correctText?: string;
    incorrectText?: string;
    // Graded free answers: the accepted answer the recording was matched to.
    matchedText?: string;
    onTake?: (outcome: ScoreOutcome, failedTakes: number) => void;
    // Pass, or any take when not graded.
    onComplete: (outcome?: ScoreOutcome) => void;
    // Multi-line activities may advance after Medium or two unsuccessful takes.
    onMoveOn?: () => void;
    skipHint?: string;
    // After two failed graded takes the player offers Skip.
    onSkippable?: () => void;
}

function formatElapsed(seconds: number): string {
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

// Tap Start → recording (clearly marked, tap Stop) → checking → result. Shared by
// every exercise that ends in the student speaking. Fake audio: nothing is captured.
export default function RecordAnswerPanel({
    grades,
    scoring = 'graded',
    takes = DEFAULT_TAKES,
    idleLabel = 'Start recording',
    compact = false,
    disabled = false,
    disabledHint,
    correctText,
    incorrectText,
    matchedText,
    onTake,
    onComplete,
    onMoveOn,
    onSkippable,
    skipHint = 'You can try again or choose Skip below.',
}: RecordAnswerPanelProps) {
    const [phase, setPhase] = React.useState<Phase>('idle');
    const [takeCount, setTakeCount] = React.useState(0);
    const [outcome, setOutcome] = React.useState<ScoreOutcome>('good');
    const [failedTakes, setFailedTakes] = React.useState(0);
    const [completed, setCompleted] = React.useState(false);
    const [detailOpen, setDetailOpen] = React.useState(false);
    const [elapsed, setElapsed] = React.useState(0);
    const timeoutRef = React.useRef<number>();
    const intervalRef = React.useRef<number>();
    const takeFailure = useMockMediaFailure();
    const [failure, setFailure] = React.useState<MediaFailure | null>(null);

    React.useEffect(() => () => {
        window.clearTimeout(timeoutRef.current);
        window.clearInterval(intervalRef.current);
    }, []);

    React.useEffect(() => { if (disabled) setDetailOpen(false); }, [disabled]);

    const complete = (result: ScoreOutcome) => {
        if (!completed) {
            setCompleted(true);
            onComplete(result);
        }
    };

    const startRecording = () => {
        if (disabled || phase === 'recording' || phase === 'processing') {
            return;
        }
        const problem = takeFailure('microphone');
        if (problem) { setFailure(problem); return; }
        setFailure(null);
        setDetailOpen(false);
        setElapsed(0);
        setPhase('recording');
        window.clearInterval(intervalRef.current);
        intervalRef.current = window.setInterval(() => setElapsed(value => value + 1), 1000);
    };

    const checkRecording = () => {
        window.clearInterval(intervalRef.current);
        setFailure(null);
        setPhase('processing');
        timeoutRef.current = window.setTimeout(() => {
            const problem = scoring === 'none' ? takeFailure('silence', 'network') : takeFailure('silence', 'scoring', 'network');
            if (problem) {
                setPhase('idle');
                setFailure(problem);
                return;
            }
            const next = takes[Math.min(takeCount, takes.length - 1)] ?? 'good';
            const failed = scoring === 'graded' && next !== 'good' ? failedTakes + 1 : failedTakes;
            setTakeCount(takeCount + 1);
            setOutcome(next);
            setFailedTakes(failed);
            setPhase('result');
            onTake?.(next, failed);
            if (scoring !== 'graded' || next === 'good') {
                complete(next);
            } else if (failed === 2) {
                onSkippable?.();
            }
        }, PROCESSING_MS);
    };

    const takeGrades = gradesForOutcome(grades, outcome);
    const score = speakingScore(takeGrades);
    const fallback = scoreMessage(outcome);
    const marginTop = compact ? 1.5 : 3;

    const pillSx = {
        fontFamily: INTER, fontWeight: 700, fontSize: 14, color: BLUE, textTransform: 'none' as const,
        border: `1px solid ${RING_TRACK}`, borderRadius: '999px', minHeight: 44, px: 2.5, py: 0.75,
    };
    const retryButton = (label: string) => (
        <Button startIcon={label === 'Try again' ? <ReplayRoundedIcon /> : <MicRoundedIcon />} disabled={disabled} onClick={() => setPhase('idle')} sx={pillSx}>
            {label}
        </Button>
    );

    if (failure) {
        return <Box sx={{ mt: marginTop }}><MediaErrorNotice failure={failure} onRetry={
            failure === 'scoring' || failure === 'network' ? checkRecording : startRecording
        } /></Box>;
    }

    // Completed conversation turns retain details without inactive recorder controls.
    if (compact && disabled) {
        if (phase !== 'result') return null;
        return <Box sx={{ mt: 1 }} data-completed-recording>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: SLATE }}>{score}%</Typography>
                <Button onClick={() => setDetailOpen(value => !value)} aria-expanded={detailOpen}
                    sx={{ minHeight: 44, fontFamily: INTER, fontSize: 13, textTransform: 'none' }}>See details</Button>
            </Stack>
            <Collapse in={detailOpen}><PronunciationDetail grades={takeGrades} /></Collapse>
        </Box>;
    }

    if (phase === 'result') {
        if (scoring === 'none') {
            return (
                <Stack alignItems="center" sx={{ mt: marginTop }}>
                    <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 40 }} />
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY, mt: 1, textAlign: 'center' }}>
                        {correctText ?? 'Got it — 说得好!'}
                    </Typography>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.5, textAlign: 'center' }}>
                        This one is not scored — speaking up is the whole point.
                    </Typography>
                    <Box sx={{ mt: 2 }}>{retryButton('Record again')}</Box>
                </Stack>
            );
        }

        const passed = scoring === 'feedback' || outcome === 'good';
        const title = scoring === 'feedback'
            ? correctText ?? 'Got it — 说得好!'
            : outcome === 'good' ? correctText ?? fallback.title
                : outcome === 'bad' ? incorrectText ?? fallback.title : fallback.title;
        const subtitle = scoring === 'feedback'
            ? 'Your score is feedback only — you can continue whenever you are ready.'
            : outcome === 'bad' && incorrectText ? 'Take your time, then give it another try.' : fallback.subtitle;

        return (
            <Stack alignItems="center" sx={{ mt: marginTop }}>
                <ProgressRing percent={score} size={compact ? 60 : 84} thickness={4} showLabel />
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: compact ? 15 : 18, color: passed ? NAVY : outcome === 'bad' ? ERROR_TEXT : NAVY, mt: 1.25, textAlign: 'center' }}>
                    {title}
                </Typography>
                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.5, textAlign: 'center' }}>
                    {subtitle}
                </Typography>
                {matchedText && (outcome === 'good' || failedTakes >= 2) && (
                    <Typography sx={{ fontFamily: INTER, fontSize: 12.5, color: SLATE, mt: 1, textAlign: 'center' }}>
                        Matched answer: <Box component="span" sx={{ fontFamily: POPPINS, color: NAVY }}>{matchedText}</Box>
                    </Typography>
                )}
                {(!matchedText || outcome === 'good' || failedTakes >= 2) && <>
                <ButtonBase
                    onClick={() => setDetailOpen(prev => !prev)}
                    aria-expanded={detailOpen}
                    sx={{ minHeight: 44, mt: 1, borderRadius: '999px', px: 1.5, py: 0.5, fontFamily: INTER, fontWeight: 700, fontSize: 13, color: BLUE, gap: 0.5 }}
                >
                    See details
                    <ExpandMoreRoundedIcon sx={{ fontSize: 20, transform: detailOpen ? 'rotate(180deg)' : 'none', transition: 'transform 200ms ease' }} />
                </ButtonBase>
                <Collapse in={detailOpen} sx={{ width: '100%' }}>
                    <Box sx={{ mt: 1.5 }}>
                        <PronunciationDetail grades={takeGrades} />
                    </Box>
                </Collapse>
                </>}
                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                    {scoring === 'graded' && outcome !== 'good' && !completed ? (
                        retryButton('Try again')
                    ) : (
                        retryButton('Record again')
                    )}
                    {scoring === 'graded' && !completed && onMoveOn && (outcome === 'medium' || failedTakes >= 2) && (
                        <Button onClick={onMoveOn} sx={{ ...pillSx, color: SLATE }}>Move on</Button>
                    )}
                </Stack>
                {scoring === 'graded' && !completed && failedTakes >= 2 && onSkippable && !onMoveOn && (
                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 1.25, textAlign: 'center' }}>
                        {skipHint}
                    </Typography>
                )}
            </Stack>
        );
    }

    const isRecording = phase === 'recording';
    const isProcessing = phase === 'processing';

    return (
        <Box sx={{ mt: marginTop, '@media (prefers-reduced-motion: reduce)': { '&, & *': { animation: 'none !important', transition: 'none !important' } } }}>
            {isRecording && (
                <Box role="status" sx={{ mb: 1.5, p: compact ? 1.25 : 1.75, borderRadius: '14px', backgroundColor: `${RED}12` }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: RED, animation: `${recordingDot} 1s ease-in-out infinite` }} />
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: ERROR_TEXT, flex: 1 }}>
                            Recording… speak, then tap Stop
                        </Typography>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: ERROR_TEXT }}>{formatElapsed(elapsed)}</Typography>
                    </Stack>
                    <LinearProgress sx={{ height: 6, borderRadius: '999px', backgroundColor: RING_TRACK, '& .MuiLinearProgress-bar': { backgroundColor: RED } }} />
                </Box>
            )}
            <Button
                fullWidth
                variant="contained"
                disabled={disabled || isProcessing}
                onClick={isRecording ? checkRecording : startRecording}
                startIcon={isProcessing
                    ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                    : isRecording ? <StopRoundedIcon /> : <MicRoundedIcon />}
                sx={{
                    ...yellowButtonSx,
                    ...(isRecording ? { backgroundColor: ERROR_TEXT, color: '#fff', '&:hover': { backgroundColor: ERROR_TEXT } } : {}),
                    ...(isProcessing ? { '&.Mui-disabled': { backgroundColor: BLUE, color: '#fff' } } : {}),
                    ...(compact ? { fontSize: '14px', py: 1 } : { py: 1.5 }),
                }}
            >
                {isProcessing
                    ? scoring === 'none' ? 'Saving your recording…' : 'Checking your pronunciation…'
                    : isRecording ? 'Stop recording' : idleLabel}
            </Button>
            {!isRecording && !isProcessing && (
                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, textAlign: 'center', mt: 1 }}>
                    {disabled && disabledHint ? disabledHint : 'Tap to start. Tap Stop when you finish speaking.'}
                </Typography>
            )}
        </Box>
    );
}
